import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { endpoints, apiBaseUrl } from '../../APIs/endpoints';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Debounce utility (unchanged)
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Dashed grid plugin (unchanged)
const dashedGridPlugin = {
  id: "dashedGrid",
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    const y = scales.y;
    if (!y || !chartArea) return;

    ctx.save();
    ctx.setLineDash([10, 6]);
    ctx.strokeStyle = "#E2E2E2";
    ctx.lineWidth = 1;

    y.ticks.forEach((_, i) => {
      const yPos = y.getPixelForTick(i);
      if (yPos >= chartArea.top && yPos <= chartArea.bottom) {
        ctx.beginPath();
        ctx.moveTo(chartArea.left, yPos);
        ctx.lineTo(chartArea.right, yPos);
        ctx.stroke();
      }
    });
    ctx.restore();
  },
};

const ComparitiveAnalysis = ({ show, onClose, subject, allSubjects, studentId, DatePicker, school_name }) => {
  const [activeTab, setActiveTab] = useState("Attention Span");
  const [firstSubject, setFirstSubject] = useState(subject || "Select Subject");
  const [secondSubject, setSecondSubject] = useState("Select Subject");
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isFirstDropdownOpen, setIsFirstDropdownOpen] = useState(false);
  const [isSecondDropdownOpen, setIsSecondDropdownOpen] = useState(false);
  const [isFirstDropdownScrolling, setIsFirstDropdownScrolling] = useState(false); // New state
  const [isSecondDropdownScrolling, setIsSecondDropdownScrolling] = useState(false); // New state
  const [analyticsData, setAnalyticsData] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [bucketLabels, setBucketLabels] = useState([]);
  const [filteredBuckets, setFilteredBuckets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const firstDropdownRef = useRef(null);
  const secondDropdownRef = useRef(null);
  const chartRef = useRef(null);
  const [customTooltip, setCustomTooltip] = useState(null);
  const [legendTooltip, setLegendTooltip] = useState(null);
  const [apiDateRange, setApiDateRange] = useState({ start: null, end: null });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

  const { startDate, endDate } = DatePicker || {};
  const token = localStorage.getItem("access_token");

  const formatDateForAPI = (date) => {
    if (!date) return null;
    return date instanceof Date ? date.toISOString().slice(0, 10) : date;
  };

  const toIndianDateFormat = (dateStr, isTimeOnly = false) => {
    if (!dateStr) return null;

    // Handle full ISO string with time
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
      if (isTimeOnly) {
        return dateStr.split('T')[1].slice(0, 8); // e.g., "10:30:00"
      }
      const [year, month, day] = dateStr.split('T')[0].split('-');
      return `${day}-${month}-${year}`; // e.g., "01-01-2025"
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${month}-${year}`;
    }
    return dateStr;
  };

  const isBucketInDateRange = (bucketDates, start, end) => {
    if (!start || !end || !bucketDates.length) return true;
    const startMs = new Date(start).setHours(0, 0, 0, 0);
    const endMs = new Date(end).setHours(23, 59, 59, 999);
    const isSingleDayRange = startMs === endMs;

    return bucketDates.some(date => {
      const dateOnly = date.split('T')[0]; // Ensure comparison is only on date part
      const dateMs = new Date(dateOnly).setHours(0, 0, 0, 0);
      if (isSingleDayRange) {
        return dateMs === startMs;
      }
      return dateMs >= startMs && dateMs <= endMs;
    });
  };

  useEffect(() => {
    if (!token) {
      setError("No authentication token found. Please log in.");
      setIsLoading(false);
      return;
    }

    if (!school_name) {
      setError("School name is required.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const queryParams = new URLSearchParams({ school: school_name }).toString();
    fetch(`${apiBaseUrl}${endpoints.SUBJECT_LIST}/?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        if (res.status === 401) {
          console.error("Unauthorized: Invalid or expired token");
          setError("Authentication failed. Please log in again.");
          localStorage.removeItem("access_token");
          return { subjects: [] };
        }
        if (!res.ok) {
          const text = await res.text();
          try {
            const err = JSON.parse(text);
            throw new Error(err.detail || `HTTP ${res.status}`);
          } catch {
            throw new Error(`HTTP ${res.status}: ${text.slice(0, 50)}...`);
          }
        }
        if (!res.headers.get("content-type")?.includes("application/json")) {
          const text = await res.text();
          throw new Error(`Expected JSON, got: ${text.slice(0, 50)}...`);
        }
        return res.json();
      })
      .then((data) => {
        const subjects = Array.isArray(data)
          ? data.map((name) => ({ name }))
          : Array.isArray(data.subjects)
            ? data.subjects
            : [];
        if (subject && !subjects.some((s) => s.name === subject)) {
          subjects.push({ name: subject });
        }
        setSubjectsList(subjects);
        setIsLoading(false);
        setError(null);
      })
      .catch((error) => {
        console.error("Error fetching subjects:", error);
        setError("Failed to load subjects. Please try again.");
        setSubjectsList([]);
        setIsLoading(false);
      });
  }, [token, subject, school_name]);

  // Fetch analytics data (modified to filter buckets and handle date range display)
  useEffect(() => {
    if (!token) {
      setError("No authentication token found. Please log in.");
      setAnalyticsData([]);
      return;
    }

    if (!studentId || !school_name) {
      setError("Student ID and school name are required.");
      setAnalyticsData([]);
      return;
    }

    setIsLoading(true);
    const queryParamsObj = { school: school_name, mode: 'daily_summary' };
    if (startDate && endDate) {
      queryParamsObj.start_date = formatDateForAPI(startDate);
      queryParamsObj.end_date = formatDateForAPI(endDate);
    }
    const queryParams = new URLSearchParams(queryParamsObj).toString();

    fetch(`${apiBaseUrl}${endpoints.COMPARATIVE_ANALYTICS}/${studentId}/?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        if (res.status === 401) {
          console.error("Unauthorized: Invalid or expired token");
          setError("Authentication failed. Please log in again.");
          localStorage.removeItem("access_token");
          return { data: {} };
        }
        if (!res.ok) {
          const text = await res.text();
          console.error("Analytics API Error:", text.slice(0, 50));
          throw new Error(`HTTP ${res.status}: ${text.slice(0, 50)}...`);
        }
        if (!res.headers.get("content-type")?.includes("application/json")) {
          const text = await res.text();
          console.error("Expected JSON, got:", text.slice(0, 50));
          throw new Error(`Expected JSON, got: ${text.slice(0, 50)}...`);
        }
        return res.json();
      })
      .then((data) => {
        const allBucketsKeys = Object.keys(data.data && Object.values(data.data)[0] || {});

        setApiDateRange({ start: data.start_date, end: data.end_date });


        // Determine if it's a single day selection for date formatting
        const isSingleDayRange = startDate && endDate && formatDateForAPI(startDate) === formatDateForAPI(endDate);
        const selectedDateCount = startDate && endDate ? (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24) + 1 : 0;

        let dynamicFilteredBuckets = [];
        let calculatedBucketLabels = [];

        if (selectedDateCount <= 7 && selectedDateCount > 0) {
          const dateMap = new Map();

          Object.values(data.data || {}).forEach(subjectData => {
            Object.values(subjectData).forEach(bucket => {
              bucket.dates.forEach(dateStr => {
                const dateOnly = dateStr.split('T')[0]; // Get YYYY-MM-DD
                const formattedDate = toIndianDateFormat(dateOnly); // DD-MM-YYYY
                if (!dateMap.has(formattedDate)) {
                  dateMap.set(formattedDate, {
                    attention: 0,
                    engage: 0,
                    records: 0,
                    originalDates: [],
                    subjects: {}, // To store data for each subject for this date
                  });
                }
                const currentData = dateMap.get(formattedDate);
                // Aggregate for the overall bucket label logic (if needed, though here we're doing per subject)
                currentData.records += bucket.records;
                currentData.originalDates.push(dateStr);
              });
            });
          });

          // Sort dates for display
          const sortedDates = Array.from(dateMap.keys()).sort((a, b) => {
            const [dA, mA, yA] = a.split('-');
            const [dB, mB, yB] = b.split('-');
            return new Date(`${yA}-${mA}-${dA}`) - new Date(`${yB}-${mB}-${dB}`);
          });

          dynamicFilteredBuckets = sortedDates; // These are now the date labels
          calculatedBucketLabels = sortedDates;

          const analytics = Object.keys(data.data || {}).map((subjectName) => {
            const subjectBuckets = data.data[subjectName] || {};
            const subjectDateData = new Map(); // Map: "DD-MM-YYYY" -> { attention, engage }

            Object.values(subjectBuckets).forEach(bucket => {
              if (bucket.records > 0) {
                bucket.dates.forEach(dateStr => {
                  const dateOnly = dateStr.split('T')[0];
                  const formattedDate = toIndianDateFormat(dateOnly);
                  if (formattedDate) {
                    // Accumulate attention and engage for each specific date
                    const existing = subjectDateData.get(formattedDate) || { attention: 0, engage: 0, count: 0 };
                    subjectDateData.set(formattedDate, {
                      attention: existing.attention + bucket.attention, // Summing up might not be correct for average,
                      engage: existing.engage + bucket.engage,           // but following the structure of existing data
                      count: existing.count + 1,
                    });
                  }
                });
              }
            });

            // Map sortedDates to corresponding attention/engagement values
            const engagementData = dynamicFilteredBuckets.map(dateLabel => {
              const entry = subjectDateData.get(dateLabel);
              // If multiple records for a single day, average them. If not, use the value directly.
              return entry ? (entry.count > 0 ? (entry.engage / entry.count) : 0) : 0;
            });

            const attentionData = dynamicFilteredBuckets.map(dateLabel => {
              const entry = subjectDateData.get(dateLabel);
              return entry ? (entry.count > 0 ? (entry.attention / entry.count) : 0) : 0;
            });

            const totalRecords = Object.values(subjectBuckets).reduce((sum, b) => sum + b.records, 0);

            return {
              name: subjectName,
              engagement: engagementData,
              attention: attentionData,
              hasRecords: totalRecords > 0,
            };
          });
          setAnalyticsData(analytics);

        } else {
          // Original logic for more than 7 dates or if start/end dates are not defined, or if single day has many records
          const firstSubjectWithData = Object.keys(data.data || {})[0];
          const allBuckets = ['bucket1', 'bucket2', 'bucket3', 'bucket4', 'bucket5', 'bucket6', 'bucket7'];

          dynamicFilteredBuckets = allBuckets.filter(bucket => {
            if (!firstSubjectWithData || !data.data[firstSubjectWithData][bucket]) return false;
            const dates = data.data[firstSubjectWithData][bucket]?.dates || [];
            return isBucketInDateRange(dates, startDate, endDate);
          });

          calculatedBucketLabels = dynamicFilteredBuckets.map((bucketKey, index) => {
            const subjectDataForLabels = data.data[firstSubjectWithData]?.[bucketKey];
            if (!subjectDataForLabels || subjectDataForLabels.dates.length === 0) {
              return `Bucket ${index + 1}`;
            }

            const dates = subjectDataForLabels.dates;
            const sortedDates = [...dates].sort();

            if (isSingleDayRange) {
              // For a single selected day, format as time range
              const startDateTime = sortedDates.length > 0 ? toIndianDateFormat(sortedDates[0], true) : '';
              const endDateTime = sortedDates.length > 0 ? toIndianDateFormat(sortedDates[sortedDates.length - 1], true) : '';
              return sortedDates.length > 0 ? `${startDateTime} to ${endDateTime}` : `Bucket ${index + 1}`;
            } else {
              // For a date range, format as date range
              const minDate = sortedDates.length > 0 ? toIndianDateFormat(sortedDates[0].split('T')[0]) : '';
              const maxDate = sortedDates.length > 0 ? toIndianDateFormat(sortedDates[sortedDates.length - 1].split('T')[0]) : '';
              return sortedDates.length > 0 ? `${minDate} to ${maxDate}` : `Bucket ${index + 1}`;
            }
          });

          const analytics = Object.keys(data.data || {}).map((subjectName) => {
            const bucketsData = dynamicFilteredBuckets.map(bucket => {
              const bucketData = data.data[subjectName][bucket] || {};
              return {
                engage: bucketData.engage || 0,
                attention: bucketData.attention || 0,
                dates: bucketData.dates || [],
                records: bucketData.records || 0,
              };
            });

            const totalRecords = bucketsData.reduce((sum, b) => sum + b.records, 0);

            return {
              name: subjectName,
              engagement: bucketsData.map(b => b.engage),
              attention: bucketsData.map(b => b.attention),
              bucketDates: dynamicFilteredBuckets.reduce((acc, bucket, index) => ({
                ...acc,
                [bucket]: bucketsData[index]?.dates || [],
              }), {}),
              hasRecords: totalRecords > 0,
            };
          });
          setAnalyticsData(analytics);
        }

        setFilteredBuckets(dynamicFilteredBuckets);
        setBucketLabels(calculatedBucketLabels);
        setError(null);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching analytics:", error);
        setError("Failed to load analytics data. Please try again.");
        setAnalyticsData([]);
        setIsLoading(false);
      });
  }, [token, studentId, school_name, startDate, endDate]);


  // Auto-select first subject (unchanged)
  useEffect(() => {
    if (subject) {
      setFirstSubject(subject);
      setSecondSubject("Select Subject");
      setShowAnalysis(false);
    }
  }, [subject]);

  // Update showAnalysis when subjects change (unchanged)
  useEffect(() => {
    const bothSelected = firstSubject !== "Select Subject" && secondSubject !== "Select Subject";
    setShowAnalysis(bothSelected);
  }, [firstSubject, secondSubject]);

  // Close dropdowns on outside click and reset scrolling state
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (firstDropdownRef.current && !firstDropdownRef.current.contains(event.target)) {
        setIsFirstDropdownOpen(false);
        setIsFirstDropdownScrolling(false); // Reset scrolling state
      }
      if (secondDropdownRef.current && !secondDropdownRef.current.contains(event.target)) {
        setIsSecondDropdownOpen(false);
        setIsSecondDropdownScrolling(false); // Reset scrolling state
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle screen resize with debouncing (unchanged)
  useEffect(() => {
    const handleResize = debounce(() => {
      setIsMobile(window.innerWidth <= 640);
    }, 100);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Custom event handlers for axis label tooltips on mobile only (unchanged)
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = chartRef.current;
    const canvas = chart.canvas;
    let hideTimeout;

    const getTooltipWidth = (text) => {
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.visibility = 'hidden';
      tempDiv.style.whiteSpace = 'nowrap';
      tempDiv.style.fontSize = '12px';
      tempDiv.style.fontFamily = 'Inter, sans-serif';
      tempDiv.style.padding = '1px 3px 1px 5px';
      tempDiv.textContent = text;
      document.body.appendChild(tempDiv);
      const width = tempDiv.offsetWidth;
      document.body.removeChild(tempDiv);
      return width;
    };

    const getHoveredTick = (mouseX, mouseY) => {
      const xScale = chart.scales.x;
      const chartArea = chart.chartArea;

      if (mouseY < chartArea.bottom - 20 || mouseY > xScale.bottom + 20) return null;

      for (let index = 0; index < xScale.ticks.length; index++) {
        const tickX = xScale.getPixelForTick(index);
        if (Math.abs(mouseX - tickX) < 50) {
          const tooltipText = bucketLabels[index] || `Bucket ${index + 1}`;
          const tooltipWidth = getTooltipWidth(tooltipText);
          const halfWidth = tooltipWidth / 2;

          let adjustedLeft = tickX;
          if (tickX - halfWidth < chartArea.left) {
            adjustedLeft = chartArea.left + halfWidth;
          } else if (tickX + halfWidth > chartArea.right) {
            adjustedLeft = chartArea.right - halfWidth;
          }

          return {
            text: tooltipText,
            left: adjustedLeft,
            top: xScale.bottom - 10,
            index,
          };
        }
      }
      return null;
    };

    const handleMove = (event, isTouch = false) => {
      const e = isTouch ? event.touches[0] : event;
      if (!e) return;

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const hovered = getHoveredTick(mouseX, mouseY);
      setCustomTooltip(hovered);

      if (hovered && isTouch) {
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => setCustomTooltip(null), 3000);
      }
    };

    const handleMouseMove = (event) => handleMove(event, false);
    const handleTouchStart = (event) => {
      event.preventDefault();
      handleMove(event, true);
    };
    const handleTouchEnd = () => {
      clearTimeout(hideTimeout);
      setCustomTooltip(null);
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchend', handleTouchEnd);
      clearTimeout(hideTimeout);
    };
  }, [chartRef, bucketLabels]);

  const getTabData = useCallback((subjectName) => {
    const subjectData = Array.isArray(analyticsData) ? analyticsData.find((d) => d.name === subjectName) : null;
    if (!subjectData || !subjectData.hasRecords) {
      return Array(filteredBuckets.length).fill(0); // Return all zeros if no records
    }
    if (activeTab === "Engagement") {
      return subjectData.engagement || Array(filteredBuckets.length).fill(0);
    } else {
      return subjectData.attention || Array(filteredBuckets.length).fill(0);
    }
  }, [analyticsData, activeTab, filteredBuckets]);

  // Define subj1 and subj2 (unchanged)
  const subj1 = Array.isArray(subjectsList) ? subjectsList.find((s) => s.name === firstSubject) || null : null;
  const subj2 =
    secondSubject !== "Select Subject" && Array.isArray(subjectsList)
      ? subjectsList.find((s) => s.name === secondSubject) || null
      : null;

  // Define chartData (unchanged)
  const chartData = useMemo(() => {
    const datasets = [];
    if (subj1) {
      datasets.push({
        label: subj1.name,
        data: getTabData(subj1.name),
        backgroundColor: "#16DBCC",
        borderRadius: 8,
      });
    }
    if (subj2) {
      datasets.push({
        label: subj2.name,
        data: getTabData(subj2.name),
        backgroundColor: "#B325E5",
        borderRadius: 8,
      });
    }
    return {
      labels: bucketLabels,
      datasets,
    };
  }, [bucketLabels, subj1, subj2, getTabData]);

  // Chart options with responsive X-axis styling (unchanged)
  const chartOptions = useMemo(() => {
    const maxValue = Math.max(
      ...chartData.datasets
        .flatMap(dataset => dataset.data)
        .filter(value => value !== null && value !== undefined),
      10
    );
    const dynamicMax = Math.ceil(maxValue / 10) * 10;

    let stepSize;
    if (dynamicMax <= 10) {
      stepSize = 2;
    } else if (dynamicMax <= 20) {
      stepSize = 4;
    } else if (dynamicMax <= 30) {
      stepSize = 5;
    } else if (dynamicMax <= 40) {
      stepSize = 5;
    } else if (dynamicMax <= 50) {
      stepSize = 10;
    } else {
      stepSize = 10;
    }

    const isSingleDataset = chartData.datasets.length === 1;
    const categoryPercentage = isMobile ? (isSingleDataset ? 0.9 : 0.6) : (isSingleDataset ? 0.5 : 0.4); // Increased for web
    const barPercentage = isMobile ? 0.9 : (isSingleDataset ? 0.85 : 0.70); // Increased for web

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "#1F2937",
          titleColor: "#fff",
          bodyColor: "#fff",
          padding: isMobile ? 8 : 10,
          borderRadius: isMobile ? 7 : 8,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex;
              return bucketLabels[index] || `Interval ${index + 1}`;
            },
            label: (context) => {
              const bgColor = Array.isArray(context.dataset.backgroundColor)
                ? context.dataset.backgroundColor[context.dataIndex]
                : context.dataset.backgroundColor;
              if (bgColor === '#C5C5C5') return ' No Data Avaliable';
              const value = context.raw;
              return activeTab === "Engagement" ? `${value.toFixed(2)}%` : `${value.toFixed(2)} mins`;
            },
          },
        },
      },
      scales: {
        y: {
          min: 0,
          max: dynamicMax,
          grid: { display: false },
          border: { display: false },
          ticks: {
            stepSize: stepSize,
            color: "#6B7280",
            font: {
              size: isMobile ? 10 : 12,
              weight: "500",
              family: "Inter, sans-serif",
            },
            padding: isMobile ? 6 : 8,
            callback: (value) => {
              return value; // Display only the number
            },
          },
          title: {
            display: true,
            text: activeTab === "Engagement" ? "Engagement Rate (%)" : "Attention Span (min)",
            color: "#000000",
            font: {
              size: isMobile ? 11 : 14,
              weight: "500",
              family: "Inter, sans-serif",
            },
            padding: isMobile ? 4 : 8,
          },
        },
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            autoSkip: false,
            color: "#6B7280",
            font: {
              size: isMobile ? 8 : 12,
              weight: "500",
              family: "Inter, sans-serif",
            },
            padding: isMobile ? 8 : 12,
            maxRotation: isMobile ? 5 : 0,
            minRotation: isMobile ? 5 : 0,
            align: "center",
            callback: function (value, index) {
              const label = bucketLabels[index] || `Interval ${index + 1}`;
              if (isMobile) {
                return `${index + 1}`;
              }
              const parts = label.split(" to ");
              if (parts.length === 2) {
                return [parts[0].trim(), "to", parts[1].trim()];
              }
              return label;
            },
          },
          title: {
            display: true,
            text: isMobile ? "Intervals" : "Time Intervals",
            color: "#000000",
            font: {
              size: isMobile ? 11 : 14,
              weight: "500",
              family: "Inter, sans-serif",
            },
            padding: isMobile ? 0 : 2,
          },
        },
      },
      datasets: {
        bar: {
          categoryPercentage: categoryPercentage,
          barPercentage: barPercentage,
          borderRadius: {
            topLeft: 30,
            topRight: 30,
            bottomLeft: 2,
            bottomRight: 2,
          },
        },
      },
    };
  }, [activeTab, isMobile, bucketLabels, chartData]);

  const handleFirstDropdownScroll = () => {
    setIsFirstDropdownScrolling(true);
    // Optionally, reset after a delay to hide scrollbar when scrolling stops
    setTimeout(() => setIsFirstDropdownScrolling(false), 1000);
  };

  const handleSecondDropdownScroll = () => {
    setIsSecondDropdownScrolling(true);
    // Optionally, reset after a delay to hide scrollbar when scrolling stops
    setTimeout(() => setIsSecondDropdownScrolling(false), 1000);
  };

  const maxValues = chartData.labels.map((_, index) => {
    return Math.max(...chartData.datasets.map(dataset => dataset.data[index]));
  });

  const transformedDatasets = chartData.datasets.map((dataset, datasetIndex) => {
    return {
      ...dataset,
      data: dataset.data.map((val, idx) => {
        const subj1Val = subj1 ? chartData.datasets[0]?.data[idx] ?? 0 : 0;
        const subj2Val = subj2 ? chartData.datasets[1]?.data[idx] ?? 0 : 0;

        // Case 1: both values = 0 → show 10% placeholder
        if (subj1Val === 0 && subj2Val === 0) {
          return chartOptions.scales.y.max / 2;
        }

        // Case 2: this subject is 0 → match the other subject’s height
        if (val === 0) {
          const otherVal = datasetIndex === 0 ? subj2Val : subj1Val;
          return otherVal;
        }

        return val;
      }),
      backgroundColor: dataset.data.map((val, idx) => {
        const subj1Val = subj1 ? chartData.datasets[0]?.data[idx] ?? 0 : 0;
        const subj2Val = subj2 ? chartData.datasets[1]?.data[idx] ?? 0 : 0;

        if (subj1Val === 0 && subj2Val === 0) return "#C5C5C5";
        if (val === 0) return "#C5C5C5";
        return dataset.backgroundColor;
      }),
      // 🚫 Prevent hover color change
      hoverBackgroundColor: dataset.data.map((val, idx) => {
        const subj1Val = subj1 ? chartData.datasets[0]?.data[idx] ?? 0 : 0;
        const subj2Val = subj2 ? chartData.datasets[1]?.data[idx] ?? 0 : 0;

        if (subj1Val === 0 && subj2Val === 0) return "#C5C5C5";
        if (val === 0) return "#C5C5C5";
        return dataset.backgroundColor;
      }),
    };
  });


  const strikePlugin = {
    id: 'strikePlugin',
    afterDatasetsDraw(chart) {
      const { ctx } = chart;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        chart.getDatasetMeta(datasetIndex).data.forEach((bar, index) => {
          const bgColor = Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor[index]
            : dataset.backgroundColor;

          if (bgColor === '#C5C5C5') { // Only apply to gray bars (no data)
            const barTop = bar.y;
            const barBottom = bar.base;
            const barLeft = bar.x - bar.width / 2;
            const barRight = bar.x + bar.width / 2;

            ctx.save();
            ctx.beginPath();
            ctx.rect(barLeft, barTop, bar.width, barBottom - barTop); // Clip to the bar's area
            ctx.clip(); // Ensure strikes only appear within the bar

            ctx.strokeStyle = '#8C8C8C'; // Red color for strikes
            ctx.lineWidth = isMobile ? 1 : 2 ;

            const numberOfLines = 15;
            const spacing = (barBottom - barTop) / (numberOfLines + 1);
            const tiltFactor = 0.25; // Reduced tiltFactor for shorter lines

            for (let i = 1; i <= numberOfLines + 1; i++) {
              const yStart = barTop + i * spacing;

              ctx.beginPath();
              const lineMargin = isMobile ? 1 : 1.5 ;
              ctx.moveTo(barRight - lineMargin, yStart - bar.width * tiltFactor);
              ctx.lineTo(barLeft + lineMargin, yStart + bar.width * tiltFactor);
              ctx.stroke();
            }

            ctx.restore();
          }
        });
      });
    }
  };


  const NoDataText = isMobile ? "No Data" : "Subject data not available";

  const hasDataForFirstSubject = analyticsData.find(d => d.name === firstSubject)?.hasRecords;
  const hasDataForSecondSubject = analyticsData.find(d => d.name === secondSubject)?.hasRecords;

  const displayStartDate = apiDateRange.start ? toIndianDateFormat(apiDateRange.start) : 'N/A';
  const displayEndDate = apiDateRange.end ? toIndianDateFormat(apiDateRange.end) : 'N/A';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[10000] p-3 sm:p-5 md:p-8 m-0"
      style={{ display: show ? "flex" : "none" }}
    >
      <style>
        {`
          .dropdown-container {
            scrollbar-width: none; /* Hide scrollbar by default */
            -ms-overflow-style: none; /* IE and Edge */
          }
          .dropdown-container::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
          .dropdown-container.show-scrollbar {
            scrollbar-width: thin; /* Show scrollbar when scrolling */
            scrollbar-color: #6B7280 #F8F8F8;
          }
          .dropdown-container.show-scrollbar::-webkit-scrollbar {
            display: block;
            width: 8px;
          }
          .dropdown-container.show-scrollbar::-webkit-scrollbar-thumb {
            background-color: #6B7280;
            border-radius: 4px;
          }
          .dropdown-container.show-scrollbar::-webkit-scrollbar-track {
            background: #F8F8F8;
          }
        `}
      </style>
      <div className="bg-white relative flex flex-col rounded-[30px] shadow-lg p-4 sm:p-6 w-full max-w-[1114px] h-full max-h-[690px] overflow-hidden">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}

        <div
          className={`flex flex-col sm:flex-row items-center justify-between mb-4 gap-4 sm:gap-6 mt-3 sm:ml-10 ${window.innerWidth === 820 && window.innerHeight === 1180 ? "flex-col" : ""
            }`}
        >
          <div className="flex flex-col gap-4 w-full sm:w-auto sm:flex-row">
            <div className="relative w-full sm:w-[150px] mr-10" ref={firstDropdownRef}>
              <button
                onClick={() => {
                  setIsFirstDropdownOpen(!isFirstDropdownOpen);
                  setIsFirstDropdownScrolling(false); // Reset scrolling state when opening
                }}
                className="w-full sm:w-[180px] h-[46px] bg-gray-100 px-4 pr-8 rounded-md shadow-sm hover:bg-gray-300 transition flex items-center justify-between font-inter font-medium text-[14px] text-gray-800"
              >
                <span>{firstSubject}</span>
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isFirstDropdownOpen && Array.isArray(allSubjects) && (
                <div
                  className={`absolute z-10 left-0 mt-2 w-full sm:w-[180px] bg-white border font-archivo rounded-lg shadow-lg max-h-[200px] overflow-y-auto dropdown-container ${isFirstDropdownScrolling ? "show-scrollbar" : ""
                    }`}
                  onScroll={handleFirstDropdownScroll}
                >
                  <button
                    onClick={() => {
                      setFirstSubject("Select Subject");
                      setIsFirstDropdownOpen(false);
                      setIsFirstDropdownScrolling(false); // Reset scrolling state
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 font-archivo rounded text-[14px]"
                  >
                    Select Subject
                  </button>
                  {allSubjects
                    .filter((s) => s.subject !== secondSubject)
                    .map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setFirstSubject(s.subject);
                          setIsFirstDropdownOpen(false);
                          setIsFirstDropdownScrolling(false); // Reset scrolling state
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 rounded text-[14px]"
                      >
                        {s.subject}
                      </button>
                    ))}
                </div>
              )}
            </div>

            <div className="relative w-full sm:w-[150px]" ref={secondDropdownRef}>
              <button
                onClick={() => {
                  setIsSecondDropdownOpen(!isSecondDropdownOpen);
                  setIsSecondDropdownScrolling(false); // Reset scrolling state when opening
                }}
                className="w-full sm:w-[180px] h-[46px] bg-gray-100 px-4 rounded-md shadow-sm hover:bg-gray-300 transition flex items-center justify-between font-inter font-medium text-[14px] text-gray-800"
              >
                <span>{secondSubject}</span>
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isSecondDropdownOpen && Array.isArray(allSubjects) && (
                <div
                  className={`absolute z-10 left-0 mt-2 w-full sm:w-[180px] bg-white border font-archivo rounded-lg shadow-lg max-h-[200px] overflow-y-auto dropdown-container ${isSecondDropdownScrolling ? "show-scrollbar" : ""
                    }`}
                  onScroll={handleSecondDropdownScroll}
                >
                  <button
                    onClick={() => {
                      setSecondSubject("Select Subject");
                      setIsSecondDropdownOpen(false);
                      setIsSecondDropdownScrolling(false); // Reset scrolling state
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 font-archivo rounded text-[14px]"
                  >
                    Select Subject
                  </button>
                  {allSubjects
                    .filter((s) => s.subject !== firstSubject)
                    .map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSecondSubject(s.subject);
                          setIsSecondDropdownOpen(false);
                          setIsSecondDropdownScrolling(false); // Reset scrolling state
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 rounded text-[14px]"
                      >
                        {s.subject}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Date Range Display */}
          <div className="flex items-center text-sm sm:text-base font-archivo text-gray-600 absolute top-4 sm:top-12 sm:right-28">
            <span className="mr-1">Date:</span>
            <span>{displayStartDate}</span>
            <span className="mx-1">to</span>
            <span>{displayEndDate}</span>
          </div>

          <button
            onClick={onClose}
            className="w-4 h-4 sm:w-[14px] sm:h-[14px] absolute font-archivo top-[12px] right-6 sm:mr-10 sm:top-12"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M8.23 7L13.74 1.5C13.9 1.33 14 1.11 14 0.88C14 0.65 13.9 0.42 13.74 0.26C13.58 0.09 13.35 0 13.12 0C12.89 0 12.66 0.09 12.5 0.26L7 5.77L1.5 0.26C1.34 0.09 1.11 0 0.88 0C0.65 0 0.42 0.09 0.26 0.26C0.09 0.42 0 0.65 0 0.88C0 1.11 0.09 1.33 0.26 1.5L5.77 7L0.26 12.5C0.18 12.58 0.11 12.68 0.07 12.78C0.02 12.89 0 13.01 0 13.12C0 13.24 0.02 13.35 0.07 13.46C0.11 13.56 0.18 13.66 0.26 13.74C0.34 13.82 0.44 13.89 0.54 13.93C0.65 13.98 0.76 14 0.88 14C0.99 14 1.11 13.98 1.22 13.93C1.32 13.89 1.42 13.82 1.5 13.74L7 8.23L12.5 13.74C12.58 13.82 12.68 13.89 12.78 13.93C12.89 13.98 13.01 14 13.12 14C13.24 14 13.35 13.98 13.46 13.93C13.56 13.89 13.66 13.82 13.74 13.74C13.82 13.66 13.89 13.56 13.93 13.46C13.98 13.35 14 13.24 14 13.12C14 13.01 13.98 12.89 13.93 12.78C13.89 12.68 13.82 12.58 13.74 12.5L8.23 7Z"
                fill="#013369"
              />
            </svg>
          </button>
        </div>

        {!showAnalysis ? (
          <div className="flex flex-col justify-center items-center flex-1">
            <img src="/images/Layer 1.png" alt="Analysis" className="w-1/3 sm:w-1/5 mb-4" />
            <p className="text-gray-600 text-center text-sm sm:text-base mb-4 font-archivo">
              Please select subject
            </p>
          </div>
        ) : (
          <div className="mx-auto border border-gray-300 rounded-[16px] p-4 sm:p-6 flex flex-col gap-4 mt-2 w-full max-w-[987px] max-h-[525px] flex-1 sm:h-[561px] overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2 w-full sm:w-auto">
                <div className="flex flex-row items-center gap-2 sm:gap-1 p-2">
                  <h2 className="font-archivo text-base sm:text-lg">Comparative Analysis</h2>
                  <h3 className="text-[#111111] text-lg font-bold sm:flex hidden">|</h3>
                </div>
                <div className="flex flex-row items-center gap-3 sm:gap-2 flex-wrap">
                  {subj1 && (
                    <span className="flex items-center gap-1 sm:gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#16DBCC] mt-[-6px]"></span>
                      <span className="text-[#16DBCC] text-xs sm:text-sm font-archivo mt-[-4px]">{subj1.name}</span>
                    </span>
                  )}
                  {subj2 && (
                    <span className="flex items-center gap-1 sm:gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#B325EB] mt-[-6px]"></span>
                      <span className="text-[#B325EB] text-xs sm:text-sm font-archivo mt-[-4px]">{subj2.name}</span>
                    </span>
                  )}
                  {subj1 && (
                    <span className="flex items-center gap-1 sm:gap-2">

                      <span className="relative w-2 h-2 rounded-full bg-[#C5C5C5] mt-[-6px] overflow-hidden">
                        {/* top strike */}
                        <span className="absolute w-[140%] h-[0.75px] bg-[#8C8C8C] top-[30%] left-1/2 -translate-x-1/2 -rotate-45"></span>
                        {/* bottom strike */}
                        <span className="absolute w-[140%] h-[0.75px] bg-[#8C8C8C] top-[70%] left-1/2 -translate-x-1/2 -rotate-45"></span>
                      </span>

                      <span className="text-[#C5C5C5] text-xs sm:text-sm font-archivo mt-[-4px]">{NoDataText}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 sm:gap-3 rounded-3xl bg-[#F8F8F8] font-archivo px-2 sm:px-3 py-1">
                {["Attention Span", "Engagement"].map((tab) => (
                  <button
                    key={tab}
                    className={`px-4 sm:px-6 py-2 text-xs sm:text-sm rounded-full ${activeTab === tab ? "bg-white font-semibold" : "text-gray-500"
                      }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center min-h-[200px] sm:min-h-[340px] relative">
              {((subj1 && !hasDataForFirstSubject) && (!subj2 || !hasDataForSecondSubject)) ||
                ((subj2 && !hasDataForSecondSubject) && (!subj1 || !hasDataForFirstSubject)) ? (
                <div className="flex flex-col justify-center items-center">
                  <p className="text-gray-600 text-center text-sm sm:text-base font-archivo">
                    No class available for the selected subject(s) in this date range.
                  </p>
                </div>
              ) : chartData.datasets.some(dataset => dataset.data.some(val => val > 0)) ? (
                // <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={[dashedGridPlugin]} />
                <Bar
                  ref={chartRef}
                  data={{ ...chartData, datasets: transformedDatasets }}
                  options={chartOptions}
                  plugins={[dashedGridPlugin, strikePlugin]}
                />


              ) : (
                <p className="text-gray-600 text-center text-sm sm:text-base font-archivo">
                  No data available for selected subjects in this date range.
                </p>
              )}
              {customTooltip && (
                <div
                  className="absolute bg-[#1F2937] text-white px-3 py-1 rounded-md text-xs font-medium shadow-lg pointer-events-none font-inter"
                  style={{
                    left: `${customTooltip.left}px`,
                    top: `${customTooltip.top}px`,
                    transform: 'translate(-50%, 0)',
                    whiteSpace: 'nowrap',
                    paddingLeft: '5px',
                    zIndex: 1000,
                  }}
                >
                  {customTooltip.text}
                </div>
              )}
            </div>
            {isMobile && bucketLabels.length > 0 && (
              <div className="px-2 relative" style={{ marginTop: isMobile ? '-30px' : '0' }}>
                <h4 className="text-sm font-semibold text-gray-600">Date Legend</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0 text-xs text-gray-500">
                  {bucketLabels.map((label, index) => (
                    <div
                      key={index}
                      className="flex items-center truncate cursor-pointer"
                      onMouseEnter={(e) => setLegendTooltip({ text: label, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setLegendTooltip(null)}
                    >
                      <span className="font-semibold w-6 flex-shrink-0">{index + 1}:</span>
                      <span className="truncate">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {legendTooltip && (
              <div
                className="fixed bg-[#1F2937] text-white px-3 py-1 rounded-md text-xs font-medium shadow-lg pointer-events-none font-inter z-[10001] whitespace-pre-wrap"
                style={{
                  left: `${legendTooltip.x}px`,
                  top: `${legendTooltip.y + 10}px`,
                  transform: 'translateX(-50%)',
                }}
              >
                {legendTooltip.text}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparitiveAnalysis;